import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SkipSelf,
  ViewChild
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgModel
} from '@angular/forms';
import { ProjectCommunicationService } from '../../communication/project-comms.service';
import { ProjectDefaults } from '../../models/project/project-info.model';
import { ConfiguredToastrService } from '../toastr/configured-toastr.service';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { evaluate } from 'mathjs';
import { Unit, UnitService } from '../anessa-library/anessa-library/projects/unit-library/src/public-api';
import { UnitValue } from '../../models/data-structures/unit-value.model';
import { UnitValueValidation } from '../validation/validation-sub-modules/unit-value.validation';
import Utils from '../../util';
import { AnessaTranslationService } from '../translation/anessa-translation.service';
import { UserpilotReloader } from '../userpilot-reloader.component';

@Component({
  selector: 'app-unit-value',
  templateUrl: './unit-value.component.html',
  styleUrls: ['unit-value.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UnitValueComponent),
      multi: true
    },
  ],
  viewProviders: [{
    provide: ControlContainer,
    useFactory: (container: ControlContainer) => container,
    deps: [[new SkipSelf(), ControlContainer]],
  }]
})
export class UnitValueComponent extends UserpilotReloader implements OnInit, ControlValueAccessor, OnDestroy, AfterViewInit {
  @Input() isProjSettingsPage: boolean = false;
  @Input() columns = [4, 3, 3];
  @Input() label = '';

  @Input() labelTooltip = '';

  @Input() inputPlaceholder = '';
  @Input() dropdownPlaceholder = '';
  @Input() required = false;
  @Input() type = 'text';
  @Input() editable = true;
  @Input() disableValue = false;
  @Input() disableUnit = false;
  @Input() set setDisableUnit(value: boolean) {
    this.disableUnit = value;
    setTimeout(() => {
      this.updateValueValidity();
    });
  }
  @Input() disableClear = false;
  @Input() percentSuffix = '';
  @Input() customSuffix: string | null = null;
  @Input() currentUnit = ''
  // List of unit display names to not add custom suffix to
  @Input() customSuffixIgnoredUnits: string[] = [];
  @Input() minFractionDigits?: number;
  @Input() uvUnitMinWidth = false;
  @Input() name = '';
  @Input() enableDashValidation = true;
  @Input() enableNanValidation = true;
  @Input() enableNegativeValidation = true;
  @Input() enablePercentValidation = true;
  @Input() enableWholeNumberValidation = false;
  @Input() enableZeroValidation = false;
  @Input() panelClass = '';
  /**
   * THIS WILL NOT WORK UNLESS THE UNIT TYPE BEING PASSED IN FOR THE UNITS IS IN AN ARRAY
   */
  @Input() disabledUnits: string[] = [];
  @Input() enableConversions = false;
  greaterThanValue: number | null = null;
  @Input() set setGreaterThanValue(value: number) {
    this.greaterThanValue = value;
    setTimeout(() => {
      this.updateValueValidity();
    });
  }
  lessThanValue: number | null = null;
  @Input() set setLessThanValue(value: number) {
    this.lessThanValue = value;
    setTimeout(() => {
      this.updateValueValidity();
    });
  }
  @Input() recheckValidity = new EventEmitter();
  @ViewChild('value') valueControl?: NgModel;
  @Input() forceInvalid = false;
  @Input() savingNotifier = new Subject();
  @Input() adjustOpacityOnDisable: boolean = false;
  onDestroyNotifier = new EventEmitter();
  unitName = 'unit';
  valueName = 'value';
  errorMessage = '';
  warningMessage = '';
  customErrorMessage = '';
  customWarningMessage = '';
  @Input() autofillValue: UnitValue | null = null;
  autofillMessage = 'HELPER.UNIT_VALUE.AUTOFILLED';
  @Input()
  set setCustomErrorMessage(message: string) {
    this.customErrorMessage = message;
  }
  @Input()
  set setCustomAutofillMessage(message: string) {
    this.autofillMessage = message;
  }
  @Input()
  set setCustomWarningMessage(message: string) {
    this.customWarningMessage = message;
  }
  /**
   * used for applying custom validation, defaults to @Input() name property if left blank
   */
  @Input() validationID = '';


  /**
   * Sets the unit to a set value. No checks are being performed!
   * if the unit is not part of the available ones, it will show a blank
   */
  @Input() selectUnit?: string;

  _unitValue: UnitValue = new UnitValue();
  /**
   * Boolean to control if a GUI element is show indicating the value was autofilled
   */
  autofilled = false;
  defaultsLoaded = false;

  // this input is a function so when the input is changed, the function is called
  @Input()
  set units(input: any) {
    this.defaultUnit = null;
    if (input != null) {
      let unitsSorted = false;
      this.projectDefaults = this.projectService.getProjectDefaults();
      if (typeof input === 'string') {
        if (input.length === 24 && Utils.isNumeric(input)) {
          this.unitService.unitsLoadedNotifier
            .pipe(take(1)).subscribe((data) => {
            if (data) {
              this.modelUnits = this.unitService.getUnitsByUnitTypeId(input);
              this.hideDisabledUnits();
            }
          });
        } else {
          this.unitService.unitsLoadedNotifier
            .pipe(take(1)).subscribe((data) => {
            if (data) {
              this.modelUnits = this.unitService.getUnitsByUnitTypeName(input);
              this.hideDisabledUnits();
            }
          });
        }
      } else if (input instanceof Array) {
        if (typeof input[0] === 'string') {
          this.modelUnits = [];
          for (let i = 0; i < input.length; i++) {
            this.unitService.unitsLoadedNotifier
              .pipe(take(1)).subscribe((data) => {
              if (data) {
                const unitsList = this.unitService.getUnitsByUnitTypeName(input[i]).sort(this.sortUnits);
                this.modelUnits = this.modelUnits.concat(unitsList);
                this.hideDisabledUnits();
                unitsSorted = true;
              }
            });
          }
        } else if (input[0] != null && input[0].name != null && input[0].displayName != null && input[0].unitType != null) {
          this.modelUnits = input;
        }
      } else {
        this.toastrService.error(this.translationService.get('HELPER.UNIT_VALUE.INVALID_UNITS'));
        console.error('Type of \'units\' input to <app-unit-value> component invalid');
      }
      // Sort units by specified order
      if (!unitsSorted && this.modelUnits instanceof Array) {
        this.modelUnits.sort(this.sortUnits);
      }
      if (this._unitValue != null && this.modelUnits.length === 1) {
        this._unitValue.unit = this.modelUnits[0].id;
      }
      if (this._unitValue != null && this._unitValue.unit != null && this.modelUnits.find(x => x.id === this._unitValue?.unit) == null) {
        this._unitValue.unit = null;
      }

      if (this.projectDefaults) {
        if (Array.isArray(input) && input.length === 2 &&
          input.includes('Volume_Liquid') && input.includes('Mass')
        ) {
          input = 'Volume_Liquid';
        }
        switch (input) {
          case 'Volume_Liquid':
          case '000000000000000000000011':
            this.defaultUnit = this.projectDefaults.liquid;
            break;
          case 'Mass':
          case '000000000000000000000012':
            this.defaultUnit = this.projectDefaults.solid;
            break;
          case 'Volume_Gas':
          case '000000000000000000000033':
            this.defaultUnit = this.projectDefaults.gas;
            break;
          case 'Currency':
          case '000000000000000000000020':
            this.defaultUnit = this.projectDefaults.currency;
            break;
          case 'Period':
          case '000000000000000000000013':
            this.defaultUnit = this.projectDefaults.period;
            break;
          case 'TEMPERATURE':
          case '000000000000000000000017':
            this.defaultUnit = this.projectDefaults.temperature;
            break;
          case 'CPI':
          case '000000000000000000000022':
            this.defaultUnit = this.projectDefaults.cpi.unit;
            break;
          case 'CH4_Volume':
          case '000000000000000000000006':
            if (this.projectDefaults.gas === '000000000000000000003301') {
              this.defaultUnit = '000000000000000000000606';
            } else if (this.projectDefaults.gas === '000000000000000000003302') {
              this.defaultUnit = '000000000000000000000607';
            }
            break;
          case 'Gas_Yield_Per_Raw_Feedstock':
          case '000000000000000000000043':
            if (this.projectDefaults.gas === '000000000000000000003301') {
              this.defaultUnit = '000000000000000000004207';
            } else if (this.projectDefaults.gas === '000000000000000000003302') {
              this.defaultUnit = '000000000000000000004208';
            }
            break;
          default:
            break;
        }
      }
    }
  }

  sortUnits(first: Unit, second: Unit) {
    if (Number(first.order) < Number(second.order)) {
      return -1;
    } else if (Number(second.order) < Number(first.order)) {
      return 1;
    } else {
      return 0;
    }
  }

  hideDisabledUnits() {
    if (this.selectUnit){
      for ( const myUnit of this.modelUnits) {
        if (myUnit.id === this.selectUnit) {
          this.modelUnits = [myUnit];
        }
      }
    }
    if (!this.modelUnits || !this.disabledUnits || !this.disabledUnits.length) { return; }
    this.modelUnits = this.modelUnits.filter(enabledUnit => {
      return !this.disabledUnits.includes(enabledUnit.displayName) && !this.disabledUnits.includes(enabledUnit.id ?? '') &&
        !this.disabledUnits.includes(enabledUnit.name ?? '');
    });
  }

  // tslint:disable:member-ordering
  @Input() styleWhiteBoxes = false;

  // styling inputs
  /**
   * flex weight of the component
   * if they are equal, they will have equal width,
   * otherwise the ration will be created, eg: 1:1:1, 3:1
   * */
  @Input() inputwidth = 1;
  @Input() labelwidth = 1;
  /**
   *
   * @type {string}
   * Sets the flex styling. Current available styles:
   *  - column
   */
  @Input() flexStyle = '';
  @Input() labelFontSize = '';
  @Input() fontSize = '';


  @Input() unitwidth = 1;
  @Input() unitOnly = false;

  modelUnits: Unit[] = [];
  valueDisplay = '';

  showClear = false;

  previousUnit: string | null = null;

  @Output() unitValueChange = new EventEmitter<UnitValue>();
  @Output() valueChange = new EventEmitter<UnitValue>();
  @Output() unitChange = new EventEmitter<UnitValue>();

  projectDefaults?: ProjectDefaults;
  defaultUnit: string | null = null;
  /**
   * Used to clear autofilled values.
   */
  clearClicked = false;

  @Input() coloredInputs = false;
  @Input() defaultInputs: UnitValue | null = null;
  inputsColorStyle = {
    value: {
      anessa_dark_blue: false,
      anessa_blue: false,
      anessa_green: false,
    },
    unit: {
      anessa_dark_blue: false,
      anessa_blue: false,
    }
  };

  /** trigger for translation pipe */
  trigger?: string;

  constructor(
    private projectService: ProjectCommunicationService,
    private unitService: UnitService,
    private toastrService: ConfiguredToastrService,
    private elementRef: ElementRef,
    public translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    if (!this._unitValue) {
      this._unitValue = new UnitValue();
    } else {
      this.previousUnit = this._unitValue.unit;
    }
    let name;
    try {
      name = this.elementRef.nativeElement.attributes.name.value;
    } catch (e) {
      name = '';
    }
    if (name === null || name === undefined) {
      name = '';
    }
    // if @Input() is set, it takes priority else use name attribute
    if (this.name !== '') {
      name = this.name;
    }

    this.valueName = name + 'value';
    this.unitName = name + 'name';

    /**
     * sets the unit to null if there is not value. Should be emitted
     * when saving (before the save call)
     */
    this.savingNotifier.pipe(takeUntil(this.onDestroyNotifier)).subscribe(() => {
      if (this._unitValue.value === null) {
        this._unitValue.unit = null;
        this.onChanged();
      }
    });
    this.recheckValidity.pipe(takeUntil(this.onDestroyNotifier)).subscribe(() => {
      this.updateValueValidity();
    })
  }

  ngAfterViewInit() {
    setTimeout( () => this.setInputsColors() );
  }

  setInputsColors() {
    this.inputsColorStyle = {
      value: {anessa_dark_blue: false, anessa_blue: false, anessa_green: false},
      unit: {anessa_dark_blue: false, anessa_blue: false}
    };
    this.autofilled = false;
    if (this.autofillValue && new UnitValueValidation(this.autofillValue).isValid()) {
      if (this.autofillValue.value === this._unitValue.value && this.autofillValue.unit === this._unitValue.unit) {
        this.inputsColorStyle.value.anessa_green = true;
        this.autofilled = true;
      }
    }
    if (!this.coloredInputs) { return; }
    if (this.defaultInputs && this._unitValue.value === this.defaultInputs.value) {
      this.inputsColorStyle.value.anessa_dark_blue = true;
    } else {
      this.inputsColorStyle.value.anessa_blue = true;
    }
    if (this.defaultInputs && this._unitValue.unit === this.defaultInputs.unit) {
      this.inputsColorStyle.unit.anessa_dark_blue = true;
    } else {
      this.inputsColorStyle.unit.anessa_blue = true;
    }
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  /**
   * Converts a number to string with configured number of decimal places.
   * Should not truncate the values entered by the user
   *
   * @param num value to convert to a string
   */
  getDisplayValue(num: number) {
    if (Utils.isNumeric(this.minFractionDigits)) {
      return num.toLocaleString(undefined, {minimumFractionDigits: this.minFractionDigits});
    }
    return num.toString();
  }

  valueLostFocus() {
    this.addCommas();
    this.touched();
  }

  valueChanged(newVal: string) {
    if (newVal) {
      if (Utils.isNumeric(newVal)) {
        this._unitValue.value = +newVal;
      }
    } else {
      this._unitValue.value = null;
    }
    if (this._unitValue.value !== null && !this._unitValue.unit && newVal !== '-') {
      this._unitValue.unit = this.defaultUnit;
      this.previousUnit = this._unitValue.unit;
    }
    this.removeCommas();
    this.updateValueValidity();
    this.addCommas(true);
    this.setInputsColors();
    this.valueChange.emit();
  }

  addCommas(useDisplayValue: boolean = false) {
    if (this.valueDisplay && this.valueDisplay.length > 0 && this.valueDisplay !== '-') {
      const conversion = Number(this.valueDisplay.replace(/,/gi, ''));
      if (!isNaN(conversion)) {
        this._unitValue.value = Number(this.valueDisplay.replace(/,/gi, ''));
        let numberComponents: string[];
        if (useDisplayValue) {
          numberComponents = this.valueDisplay.split('.');
        } else {
          numberComponents = this.getDisplayValue(this._unitValue.value).split('.');
        }
        const leftSide = numberComponents[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        numberComponents.splice(0, 1, leftSide);
        this.valueDisplay = numberComponents.join('.');
        this.checkForNegativeZero();
      } else {
        this._unitValue.value = null;
      }
    }
  }

  removeCommas() {
    if (this.valueDisplay && this.valueDisplay.length > 0 && this.valueDisplay !== '-') {
      this._unitValue.value = Number(this.valueDisplay.replace(/,/gi, ''));
      this.valueDisplay = this.valueDisplay.replace(/,/gi, '');
      if (this.modelUnits.length === 1) {
        this._unitValue.unit = this.modelUnits[0].id;
      }
    }

    this.handleOneUnitCase();
    this.touched();
  }

  handleOneUnitCase() {
    if (this.modelUnits.length === 1) {
      this._unitValue.unit = this.modelUnits[0].id;
    }
  }

  getDisplayName(unit: Unit) {
    if (!unit.translationId) {
      return '';
    }

    let result = this.translationService.get(unit.translationId)
    result = Unit.parseUnitDisplayName(result);
    if (result === '%') {
      result = result + this.percentSuffix;
    }

    if (
      this.customSuffix != null
      && !this.customSuffixIgnoredUnits.includes(unit.displayName)
    ) {
      result += ' ' + this.customSuffix;
    }
    return result;
  }

  getSelectedUnitDisplayName(id: string) {
    let displayName = '';
    if (id && this.modelUnits && this.modelUnits.length > 0) {
      const unit =  this.modelUnits.find(u => u.id === id);
      if (unit) {
        displayName = this.getDisplayName(unit);
      }
    }
    return displayName;
  }

  writeValue(value: UnitValue) {
    if (value && isNaN(Number(value.value))) {
      return;
    }
    value = this.checkForAndSetDefaultValues(value);
    this._unitValue = value;
    if (value === null) {
      return;
    }

    if (this.unitOnly && value.value == null) {
      this._unitValue.value = 1;
    }

    if (value.value != null && !isNaN(value.value)) {
      this.valueDisplay = this._unitValue.value?.toString() ?? '';
      // special case -0
      this.checkForNegativeZero();

      this.addCommas();
    }

    if (this.modelUnits && this.modelUnits.length === 1) {
      this._unitValue.unit = this.modelUnits[0].id;
    }
    if (this._unitValue.unit != null
      && this.modelUnits != null
      && this.modelUnits.length > 1
      && this.modelUnits.find(x => x.id === this._unitValue.unit) == null
    ) {
      this._unitValue.unit = null;
    }
    this.previousUnit = this._unitValue.unit;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  onChanged() {
    this.removeCommas();
    this.unitValueChange.emit(this._unitValue);
    // update the value so any conversion done on unit conversion is noticed
    this.writeValue(this._unitValue);
    this.addCommas();
    this.setInputsColors();
    this.updateValueValidity();
  }

  /**
   * Clears both the unit and value for the current unit value inputs, then triggers the changes made notifiers
   */
  onClearUnitValue() {
    this.clearClicked = true;
    if (!this.disableUnit) this._unitValue.unit = null;
    if (!this.disableValue) {
      this._unitValue.value = null;
      this.valueDisplay = '';
    }
    this.onChanged();
  }

  /**
   * does not work for currency, also this can be replaced with the Unit class functionality
   */
  onUnitChanged() {
    // If the unitvalue is unit only valueControl will be null
    if (this.valueControl) {
      if (this.enableConversions) {
        const oldUnit = this.unitService.getUnitById(this.previousUnit ?? '');
        const newUnit = this.unitService.getUnitById(this._unitValue.unit ?? '');

        // Flip calculation to get from base to desired unit
        if (newUnit && newUnit.factor && oldUnit && oldUnit.factor
        ) {
          let fromBase = newUnit.factor;
          if (fromBase.includes('/')) {
            fromBase = fromBase.replace(/\//g, '*');
          } else if (fromBase.includes('*')) {
            fromBase = fromBase.replace(/\*/g, '/');
          }
          // Convert to base factor (V), then to new unit
          const baseValue = evaluate(oldUnit.factor.replace(/V/g, this._unitValue.value + ''));
          this._unitValue.value = evaluate(fromBase.replace(/V/g, baseValue));
          this.writeValue(this._unitValue);
        }
        this.previousUnit = this._unitValue.unit;
        this.valueControl.control.markAsTouched();
        this.onChanged();
      }
    }
    this.unitChange.emit();
  }

  touched() {
    this.onTouchedCallback();
    this.onChangeCallback(this._unitValue);
  }
  private onChangeCallback = (value: any) => { };
  private onTouchedCallback = () => { };

  private touchValueControl() {
    if (this.valueControl && this.valueControl.control) {
      this.valueControl.control.markAsTouched();
    }
  }

  updateValueValidity() {
    if (this.valueControl && this.valueControl.control) {
      this.valueControl.control.updateValueAndValidity({onlySelf: true});
      this.touchValueControl();
    }
  }

  updateErrorMessage(data: { message: string, params: any }) {
    let tempmessage = this.translationService.get(data.message, data.params);
    if(tempmessage!='' && tempmessage!=this.errorMessage){
      this.projectService.postFromUnitValue(tempmessage, Date.now());
    }
    this.errorMessage = this.translationService.get(data.message, data.params);
  }

  updateWarningMessage(data: { message: string }) {
    this.warningMessage = this.translationService.get(data.message);
  }

  getAutofilledMessage() {
    if (this.autofilled) {
      return this.translationService.get(this.autofillMessage);
    }
  }

  private checkForAndSetDefaultValues(value: UnitValue) {
    if (this.clearClicked) {
      this.autofilled = false;
      this.autofillValue = null;
      this.clearClicked = false;
      return value;
    }
    if (this.autofillValue && new UnitValueValidation(this.autofillValue).isValid()) {
      if (!value) {
        value = new UnitValue();
      }
      if (this.autofillValue.value === value.value && this.autofillValue.unit === value.unit) {
        this.inputsColorStyle.value.anessa_green = true;
        this.autofilled = true;
      } else {
        this.inputsColorStyle.value.anessa_green = false;
        this.autofilled = false;
      }
    } else {
      this.inputsColorStyle.value.anessa_green = false;
      this.autofilled = false;
    }
      if (this.projectDefaults && this.projectDefaults.cpi
        && new UnitValueValidation(this.projectDefaults.cpi).isValid()
        && !this.isProjSettingsPage
      ) {
        if (this.defaultUnit === this.projectDefaults.cpi.unit) {
          if (!value) {
            value = new UnitValue();
          }
          this.autofilled = false;
          if (!value.unit) {
            if (this.projectDefaults.cpi.unit) {
              value.unit = this.projectDefaults.cpi.unit;
              this.autofilled = true;
            }
          }
          this.defaultsLoaded = false;
          if (!Utils.isNumeric(value.value) && !this.defaultsLoaded) {
            if (Utils.isNumeric(this.projectDefaults.cpi.value)) {
              value.value = this.projectDefaults.cpi.value;
              this.autofilled = true;
              this.defaultsLoaded = true;
            }
          }
          if (this.autofilled) {
            this.autofillValue = this.projectDefaults.cpi;
          }
        }
      }
    return value;
  }

  private checkForNegativeZero() {
    if (Object.is(this._unitValue.value, -0)) {
      this.valueDisplay = '-0';
    }
  }

}
